import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Bot, User } from "lucide-react";

const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: "Hello! I'm your AI assistant. How can I help you today?",
    },
  ]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    setMessages((prev) => [...prev, { role: "user", text: trimmed }]);
    setInput("");

    // Simulated bot response
    setTimeout(() => {
      const responses = [
        "I'd be happy to help you learn more about our AI solutions! Would you like to explore our services?",
        "Great question! Our AI platform offers chatbots, automation, analytics, and more. What interests you most?",
        "We specialize in Generative AI, Machine Learning, and NLP. Let me connect you with our team for a detailed discussion.",
        "Thanks for reaching out! You can schedule a free consultation at info@clientcodeai.in or call +91-9958821387.",
      ];
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: responses[Math.floor(Math.random() * responses.length)] },
      ]);
    }, 1000);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <>
          <motion.a
            href="https://wa.me/919718709832"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center cursor-pointer border-none"
            style={{
              background: "linear-gradient(135deg, #25D366, #1FAF8F)",
              boxShadow:
                "0 0 30px rgba(37, 211, 102, 0.7),0 0 60px rgba(31, 175, 143, 0.4)",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 32 32"
              className="w-6 h-6 fill-white"
            >
              <path d="M16 .396C7.163.396 0 7.558 0 16.396c0 2.89.756 5.598 2.08 7.947L0 32l7.88-2.053a15.94 15.94 0 0 0 8.12 2.197h.007c8.837 0 16-7.162 16-16S24.844.396 16 .396zm0 29.238a13.2 13.2 0 0 1-6.727-1.847l-.482-.286-4.676 1.217 1.25-4.56-.314-.468A13.17 13.17 0 0 1 2.8 16.396C2.8 9.16 8.763 3.196 16 3.196s13.2 5.963 13.2 13.2-5.963 13.238-13.2 13.238zm7.313-9.857c-.4-.2-2.37-1.168-2.736-1.3-.366-.132-.633-.2-.9.2s-1.033 1.3-1.266 1.566c-.233.266-.466.3-.866.1-.4-.2-1.688-.622-3.215-1.984-1.187-1.06-1.987-2.37-2.22-2.77-.233-.4-.025-.616.175-.816.18-.178.4-.466.6-.7.2-.233.266-.4.4-.666.132-.266.066-.5-.033-.7-.1-.2-.9-2.166-1.233-2.966-.325-.78-.655-.675-.9-.687l-.766-.014c-.266 0-.7.1-1.066.5s-1.4 1.366-1.4 3.333c0 1.966 1.433 3.866 1.633 4.133.2.266 2.82 4.3 6.833 6.033.955.412 1.7.658 2.28.842.958.305 1.83.262 2.52.159.77-.115 2.37-.967 2.7-1.9.333-.933.333-1.733.233-1.9-.1-.166-.366-.266-.766-.466z" />
            </svg>
          </motion.a>

          <div
            className="fixed bottom-24 right-6 z-40 w-14 h-14 rounded-full pointer-events-none"
            style={{ animation: "pulse-glow 2s ease-in-out infinite" }}
          />
        </>
      )}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center cursor-pointer border-none"
        style={{
          background: "linear-gradient(135deg, #7C4DFF, #27B8FF)",
          boxShadow: "0 0 30px rgba(124, 77, 255, 0.5), 0 0 60px rgba(39, 184, 255, 0.2)",
        }}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </motion.button>

      {/* Pulse ring on button */}
      {!isOpen && (
        <div
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full pointer-events-none"
          style={{ animation: "pulse-glow 2s ease-in-out infinite" }}
        />
      )}

      {/* Chat window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-48px)] rounded-2xl overflow-hidden"
            style={{
              background: "rgba(6, 11, 31, 0.95)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(124, 77, 255, 0.2)",
              boxShadow: "0 25px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(124, 77, 255, 0.1)",
            }}
          >
            {/* Header */}
            <div
              className="px-5 py-4 flex items-center gap-3"
              style={{
                background: "linear-gradient(135deg, rgba(124, 77, 255, 0.15), rgba(39, 184, 255, 0.1))",
                borderBottom: "1px solid rgba(124, 77, 255, 0.15)",
              }}
            >
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent to-ai-glow flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-primary" />
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm">AI Assistant</h4>
                <p className="text-xs text-emerald-400">Online</p>
              </div>
            </div>

            {/* Messages */}
            <div className="h-80 overflow-y-auto p-4 space-y-4" style={{ scrollbarWidth: "thin" }}>
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                >
                  <div
                    className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center"
                    style={{
                      background:
                        msg.role === "bot"
                          ? "linear-gradient(135deg, #7C4DFF, #27B8FF)"
                          : "rgba(124, 77, 255, 0.2)",
                    }}
                  >
                    {msg.role === "bot" ? (
                      <Bot className="w-3.5 h-3.5 text-white" />
                    ) : (
                      <User className="w-3.5 h-3.5 text-accent-light" />
                    )}
                  </div>
                  <div
                    className="max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed"
                    style={{
                      background:
                        msg.role === "bot"
                          ? "rgba(10, 16, 41, 0.8)"
                          : "linear-gradient(135deg, rgba(124, 77, 255, 0.3), rgba(39, 184, 255, 0.15))",
                      border: `1px solid ${
                        msg.role === "bot" ? "rgba(124, 77, 255, 0.1)" : "rgba(124, 77, 255, 0.25)"
                      }`,
                      color: "#E2E8F0",
                    }}
                  >
                    {msg.text}
                  </div>
                </motion.div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div
              className="p-3 flex items-center gap-2"
              style={{ borderTop: "1px solid rgba(124, 77, 255, 0.15)" }}
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                className="flex-1 bg-surface/50 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 border border-accent/10 focus:border-accent/30 focus:outline-none transition-colors"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-all cursor-pointer border-none disabled:opacity-30 disabled:cursor-not-allowed"
                style={{
                  background: input.trim()
                    ? "linear-gradient(135deg, #7C4DFF, #27B8FF)"
                    : "rgba(10, 16, 41, 0.5)",
                }}
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatbot;
